import React, { useState, useEffect } from 'react'
import { Modal, Tabs } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import conf from 'conf'
import { store as reduxStore } from 'index'

import LanguageSwitcher from 'components/cleanui/layout/TopBar/LanguageSwitcher'

import logoSVG from 'assets/images/about-app-logo.svg'
import logoSVG2 from 'assets/images/logo_no_icon.svg'

import styles from './style.module.scss'

const COLORS = {
  BLACK: '#1F2937',
  BLUE: '#0077FF',
}

const SettingIcon = ({ color = COLORS.BLACK }) => (
  <svg
    width="24px"
    height="24px"
    viewBox="0 0 24 24"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>nav-operations</title>
    <g
      id="nav-operations"
      stroke="none"
      strokeWidth="1"
      fill="none"
      fillRule="evenodd"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <g id="icon" transform="translate(3.000000, 3.000000)" stroke={color} strokeWidth="2">
        <path
          d="M7.32463,1.31731 C7.75103,-0.439103 10.249,-0.439103 10.6754,1.31731 C10.9508,2.45193 12.2507,2.99038 13.2478,2.38285 C14.7913,1.44239 16.5576,3.2087 15.6172,4.75218 C15.0096,5.74925 15.5481,7.04918 16.6827,7.32463 C18.4391,7.75103 18.4391,10.249 16.6827,10.6754 C15.5481,10.9508 15.0096,12.2507 15.6172,13.2478 C16.5576,14.7913 14.7913,16.5576 13.2478,15.6172 C12.2507,15.0096 10.9508,15.5481 10.6754,16.6827 C10.249,18.4391 7.75103,18.4391 7.32463,16.6827 C7.04918,15.5481 5.74926,15.0096 4.75219,15.6172 C3.2087,16.5576 1.44239,14.7913 2.38285,13.2478 C2.99038,12.2507 2.45193,10.9508 1.31731,10.6754 C-0.439103,10.249 -0.439103,7.75103 1.31731,7.32463 C2.45193,7.04918 2.99037,5.74926 2.38285,4.75218 C1.44239,3.2087 3.2087,1.44239 4.75219,2.38285 C5.74926,2.99037 7.04918,2.45193 7.32463,1.31731 Z"
          id="Path"
        />
        <path
          d="M12,9 C12,10.6569 10.6569,12 9,12 C7.34315,12 6,10.6569 6,9 C6,7.34315 7.34315,6 9,6 C10.6569,6 12,7.34315 12,9 Z"
          id="Path"
        />
      </g>
    </g>
  </svg>
)

const Settings = ({ open, onClose }) => {
  const { t } = useTranslation()

  const reduxState = reduxStore.getState()
  const { user } = reduxState
  const [activeTab, setActiveTab] = useState(1)

  const changeColor = (tabNum) => {
    if (activeTab === tabNum) {
      return COLORS.BLUE
    }
    return COLORS.BLACK
  }

  const handleCancel = () => {
    onClose()
  }

  const generalRender = (
    <div className={classNames(styles.generalContent)}>
      <div className={classNames(styles.content)}>
        <span>{t('topBar.profileMenu.language')}</span>
        <div className={classNames(styles.languageSwitcherBox)}>
          <LanguageSwitcher setting="settings" />
        </div>
      </div>
    </div>
  )

  const aboutRender = (
    <div className={classNames(styles.aboutContent)}>
      <div className={classNames(styles.content)}>
        <img src={logoSVG} alt="logo" className={classNames(styles.logo)} />
        <div className={classNames(styles.logoTextContent)}>
          <img src={logoSVG2} alt="logo" className={classNames(styles.logo2)} />
          <span>Version {conf.packageInfo.version}</span>
        </div>
        <span className={classNames(styles.copyright)}>
          <span>Copyright 2024 NextIntelligence.ai</span>
          <span>All rights reserved.</span>
        </span>
      </div>
    </div>
  )

  const items = [
    {
      key: 1,
      label: (
        <div className={classNames(styles.tabs1Item)}>
          <div className={classNames(styles.icon)}>
            <SettingIcon color={changeColor(1)} />
          </div>
          <div className={classNames(styles.label)} style={{ color: changeColor(1) }}>
            {t('settings.title.general')}{' '}
          </div>
        </div>
      ),
      children: (
        <Tabs
          className={classNames(styles.tabs2)}
          tabPosition="left"
          items={[
            {
              key: '1',
              label: t('settings.title.language'),
              children: generalRender,
            },
            {
              key: '2',
              label: t('settings.title.about'),
              children: aboutRender,
            },
          ]}
        />
      ),
    },
  ]

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        setActiveTab(1)
      }, 100)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const tabsAttrs = {
    tabPosition: 'left',
    className: classNames(styles.tabs, styles.tabs1),
    items,
    activeKey: activeTab,
    onChange: setActiveTab,
    centered: true,
  }

  const modalAttrs = {
    wrapClassName: classNames(styles.settingsModal, 'om-popup-base'),
    title: (
      <div className={classNames(styles.title)}>
        <span>{t('topBar.profileMenu.settings')}</span>
        <span>{user.email}</span>
      </div>
    ),
    open,
    width: 800,
    height: 600,
    closeIcon: null,
    onOk: onClose,
    onCancel: handleCancel,
    okText: t('common.button.save'),
    cancelText: t('common.button.cancel'),
    okButtonProps: {
      className: classNames('type5', 'h38', 'w100'),
      style: { minWidth: '100px' },
    },
    cancelButtonProps: {
      className: classNames('h38', 'w100'),
      style: { minWidth: '100px' },
    },
    footer: (_, { OkBtn, CancelBtn }) => (
      <div className={classNames(styles.footer)}>
        <CancelBtn />
        <OkBtn />
      </div>
    ),
  }

  return (
    <Modal {...modalAttrs}>
      <div className={classNames(styles.settings)}>
        <Tabs {...tabsAttrs} />
      </div>
    </Modal>
  )
}

export default Settings
